import {Input, Output} from "./OpenAILiveAudio";
import {Sounds} from "../../Sounds";

class Call {
    private socket: WebSocket | null = null;
    private input = new Input();
    private output = new Output();
    private lock: WakeLockSentinel | null = null;
    private acquiring = false;
    private closed = false;
    private ready = false;
    private finish: ((ready: boolean) => void) | null = null;
    private visibility = () => { void this.Wake(); };

    constructor(readonly id: string, private dotnet: any) { }

    async Connect(url: string, speed: number, muted: boolean): Promise<boolean> {
        try {
            await this.output.Start(speed,
                speaking => { if (!this.closed) void this.dotnet.invokeMethodAsync("Playback", speaking).catch(console.error); },
                () => this.Stop(),
                (ms, arrival, queued, speed) => {
                    if (!this.closed) void this.dotnet.invokeMethodAsync("AudioGap", ms, arrival, queued, speed).catch(console.error);
                },
                (requested, speed, incoming, queued) => {
                    if (!this.closed) void this.dotnet.invokeMethodAsync("AudioPace", requested, speed, incoming, queued).catch(console.error);
                });
            await this.input.Start(bytes => {
                if (!this.ready || this.socket?.readyState !== WebSocket.OPEN) return;
                if (this.socket.bufferedAmount > 24000 * 2 * 2) { this.Stop(); return; }
                this.socket.send(bytes);
            }, muted);
            if (this.closed) return false;
            const endpoint = new URL(url, location.href);
            endpoint.protocol = location.protocol === "https:" ? "wss:" : "ws:";
            const socket = this.socket = new WebSocket(endpoint);
            socket.binaryType = "arraybuffer";
            const connected = new Promise<boolean>(resolve => this.finish = resolve);
            const timeout = setTimeout(() => this.Stop(), 90000);
            socket.onmessage = event => {
                try {
                    if (event.data instanceof ArrayBuffer) this.output.Play(event.data);
                    else {
                        const command = JSON.parse(event.data);
                        if (command.type === "ready") {
                            this.ready = true;
                            this.finish?.(true);
                            this.finish = null;
                            document.addEventListener("visibilitychange", this.visibility);
                            void this.Wake();
                            Sounds.Beep();
                        } else if (command.type === "reset") this.output.Reset();
                    }
                } catch (error) { console.error(error); this.Stop(); }
            };
            socket.onclose = () => this.Stop();
            socket.onerror = () => this.Stop();
            try { return await connected; }
            finally { clearTimeout(timeout); }
        } catch (error) {
            console.error("OpenAI Live connection failed", error);
            this.Stop();
            return false;
        }
    }

    private async Wake(): Promise<void> {
        if (this.closed || !this.ready || this.lock || this.acquiring || document.visibilityState !== "visible" || !("wakeLock" in navigator)) return;
        this.acquiring = true;
        try {
            const lock = await navigator.wakeLock.request("screen");
            if (this.closed) { await lock.release(); return; }
            this.lock = lock;
            lock.addEventListener("release", () => { if (this.lock === lock) this.lock = null; });
        } catch (error) { console.warn("Could not acquire the screen wake lock.", error); }
        finally { this.acquiring = false; }
    }

    Speed(speed: number): void { this.output.Speed(speed); }
    Mute(muted: boolean): void { this.input.Mute(muted); }
    Drain(): Promise<void> { return this.output.Drain(); }

    Stop(): void {
        if (this.closed) return;
        this.closed = true;
        this.finish?.(false);
        this.finish = null;
        this.input.Stop();
        this.output.Stop();
        document.removeEventListener("visibilitychange", this.visibility);
        void this.lock?.release().catch(console.error);
        this.lock = null;
        if (this.socket) {
            this.socket.onmessage = null;
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.close();
        }
        if (this.ready) Sounds.Beep();
        this.ready = false;
    }
}

let current: Call | null = null;

export const OpenAILiveAgent = {
    async Connect(dotnet: any, id: string, url: string, speed: number, muted: boolean): Promise<boolean> {
        current?.Stop();
        const call = current = new Call(id, dotnet);
        return call.Connect(url, speed, muted);
    },
    Disconnect(id: string): void { if (current?.id === id) { current.Stop(); current = null; } },
    SetSpeed(id: string, speed: number): void { if (current?.id === id) current.Speed(speed); },
    Mute(id: string, muted: boolean): void { if (current?.id === id) current.Mute(muted); },
    async Drain(id: string): Promise<void> { if (current?.id === id) await current.Drain(); }
};
